import { Menu, X, ChevronDown } from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'

interface NavbarProps {
  scrolled: boolean
}

const products = [
  { name: 'AI教练', path: '/aicoach' },
  { name: '运动订场', path: '/venues' },
  { name: '挑战榜', path: '/ladder' },
  { name: '健身房', path: '/gym' },
  { name: '跑步机', path: '/treadmill' },
]

export default function Navbar({ scrolled }: NavbarProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [productsOpen, setProductsOpen] = useState(false)
  const location = useLocation()
  const dropdownRef = useRef<HTMLDivElement>(null)

  const isActive = (path: string) => location.pathname === path

  // 点击外部关闭下拉菜单
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setProductsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // 路由变化时关闭菜单
  useEffect(() => {
    setProductsOpen(false)
    setMobileMenuOpen(false)
  }, [location.pathname])

  return (
    <motion.nav
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={`fixed w-full z-50 transition-all duration-500 ${
        scrolled ? 'backdrop-blur-2xl border-b border-white/5' : 'bg-transparent'
      }`}
      style={scrolled ? { background: 'rgba(6, 6, 15, 0.85)' } : {}}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <img
              src="/images/lafit-logo.png"
              alt="Lafit"
              className="h-full w-auto max-h-16 transition-opacity group-hover:opacity-90"
              style={{ filter: 'brightness(1.1)' }}
            />
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-1">
            {/* Products dropdown — click to open/close */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setProductsOpen(prev => !prev)}
                className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  products.some(p => p.path === location.pathname)
                    ? 'text-blue-400 bg-blue-500/10'
                    : 'text-gray-400 hover:text-gray-200 hover:bg-white/5'
                }`}
              >
                产品
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-200 ${productsOpen ? 'rotate-180' : ''}`}
                />
              </button>
              <AnimatePresence>
                {productsOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.97 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-full left-0 mt-2 py-2 rounded-xl min-w-[160px]"
                    style={{
                      background: 'rgba(20,20,40,0.97)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
                      backdropFilter: 'blur(20px)',
                    }}
                  >
                    {products.map((product) => (
                      <Link
                        key={product.path}
                        to={product.path}
                        className={`block px-4 py-2.5 text-sm transition-colors ${
                          isActive(product.path)
                            ? 'text-blue-400 bg-blue-500/10'
                            : 'text-gray-300 hover:text-white hover:bg-white/5'
                        }`}
                      >
                        {product.name}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* 首页 */}
            <Link
              to="/"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                isActive('/')
                  ? 'text-blue-400 bg-blue-500/10'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-white/5'
              }`}
            >
              首页
            </Link>

            {/* 扫码下载（仅在首页显示） */}
            {location.pathname === '/' && (
              <Link
                to="/#download"
                className="ml-3 btn-primary text-sm"
              >
                免费体验
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden border-t border-white/5"
            style={{ background: 'rgba(6, 6, 15, 0.98)', backdropFilter: 'blur(20px)' }}
          >
            <div className="px-4 py-4 space-y-1">
              <Link
                to="/"
                className={`block px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive('/') ? 'text-blue-400 bg-blue-500/10' : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
              >
                首页
              </Link>
              <div className="px-4 py-2 text-xs text-gray-500 uppercase tracking-wider">产品</div>
              {products.map((product) => (
                <Link
                  key={product.path}
                  to={product.path}
                  className={`block px-6 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive(product.path) ? 'text-blue-400 bg-blue-500/10' : 'text-gray-300 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {product.name}
                </Link>
              ))}
              {location.pathname === '/' && (
                <Link
                  to="/#download"
                  className="btn-primary block text-center text-sm mt-4"
                >
                  免费体验
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  )
}
